import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Bookmark, CalendarClock, BriefcaseBusiness, GraduationCap, MapPin, Clock, Languages, Send, FileLock2, ShieldCheck, Pencil, Files } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CandidateAvatar, StatusBadge } from "@/components/Common";
import { HireDialog, InterviewDialog } from "@/components/RequestDialogs";
import { OfferDialog } from "@/components/OfferDialog";
import { DocRequestDialog } from "@/components/DocRequestDialog";
import { LicensesPanel } from "@/components/LicensesPanel";
import { DocRow } from "@/components/Docs";
import { CandidateForm } from "@/pages/admin/CandidateForm";
import { AvailabilityView } from "@/components/Availability";
import { useAuth } from "@/context/AuthContext";
import { api, useFetch, useShortlist } from "@/lib/api";

const OwnerDocs = ({ cid }) => {
  const [docs, setDocs] = useState(null);
  useEffect(() => { api.get(`/documents/owner/candidate/${cid}`).then((r) => setDocs(r.data)).catch(() => setDocs([])); }, [cid]);
  if (!docs) return null;
  return docs.length ? <div className="space-y-2">{docs.map((d) => <DocRow key={d.id} d={d} />)}</div>
    : <p className="text-sm text-stone-500">No shared files. Use "Request files" to ask for private documents like a full resume or license copies.</p>;
};

const Section = ({ title, icon: Icon, children }) => (
  <section className="rounded-md border border-stone-200 bg-white p-6 sm:p-8">
    <h2 className="flex items-center gap-2 font-display text-xl font-bold text-stone-900">{Icon && <Icon className="h-5 w-5 text-stone-400" strokeWidth={1.5} />}{title}</h2>
    <div className="mt-5">{children}</div>
  </section>
);

const Tags = ({ items }) => (
  <div className="flex flex-wrap gap-2">{(items || []).map((s) => <span key={s} className="rounded-sm border border-stone-200 bg-stone-50 px-2.5 py-1 text-sm text-stone-700">{s}</span>)}</div>
);

export default function CandidateDetail({ selfId }) {
  const { id: pid } = useParams();
  const id = selfId || pid;
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const self = !!selfId;
  const business = !admin && !self;
  const { data: c, setData, loading } = useFetch(`/candidates/${id}`, [id]);
  const { ids, toggle } = useShortlist();
  const [hire, setHire] = useState(false);
  const [interview, setInterview] = useState(false);
  const [offer, setOffer] = useState(false);
  const [docReq, setDocReq] = useState(false);
  const [edit, setEdit] = useState(false);

  if (loading) return <p className="text-stone-500">Loading profile…</p>;
  if (!c) return <p className="text-stone-500" data-testid="candidate-not-found">Candidate not found.</p>;
  const on = ids.includes(c.id);
  const facts = [
    ["Role category", c.category], ["Hourly rate", `$${c.hourly_rate} USD`], ["Experience", `${c.experience_years} years`],
    ["Availability", c.availability], ["Location", c.location], ["Timezone", c.timezone],
    ["English level", c.english_level], ["Languages", (c.languages || []).join(", ")],
  ];

  return (
    <div data-testid="candidate-detail">
      {!self && <Link to={admin ? "/admin/candidates" : "/app/candidates"} data-testid="back-to-candidates" className="mb-6 inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900"><ArrowLeft className="h-4 w-4" /> All candidates</Link>}
      {self && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-stone-600">This is how member businesses see your profile{c.published === false ? " once approved" : ""}.</p>
          <Button data-testid="edit-my-profile-button" onClick={() => setEdit(true)}><Pencil className="mr-2 h-4 w-4" />Edit my profile</Button>
        </div>
      )}

      <header className="rise rounded-md border border-stone-200 bg-white p-6 sm:p-8">
        <div className="flex flex-col gap-6 md:flex-row md:items-center">
          <CandidateAvatar candidate={c} className="h-28 w-28" />
          <div className="flex-1">
            <StatusBadge status={c.status} testId="candidate-status" />
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-900" data-testid="candidate-name">{c.name}</h1>
            <p className="mt-1 text-stone-600">{c.title}</p>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-stone-500">
              <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{c.location}</span>
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" />{c.timezone}</span>
              <span className="flex items-center gap-1.5"><Languages className="h-4 w-4" />{(c.languages || []).join(", ")}</span>
            </div>
          </div>
          <div className="md:text-right">
            <p className="font-display text-4xl font-extrabold text-stone-900" data-testid="candidate-rate">${c.hourly_rate}<span className="text-base font-medium text-stone-500">/hr</span></p>
            <p className="text-xs font-mono uppercase tracking-wider text-stone-500">{c.experience_years} yrs experience</p>
          </div>
        </div>
        {business && (
          <div className="mt-8 flex flex-wrap gap-3 border-t border-stone-100 pt-6">
            <Button data-testid="send-offer-button" onClick={() => setOffer(true)} className="h-11 bg-amber-600 px-6 hover:bg-amber-700"><Send className="mr-2 h-4 w-4" />Send offer</Button>
            <Button data-testid="request-hire-button" onClick={() => setHire(true)} className="h-11 px-6"><BriefcaseBusiness className="mr-2 h-4 w-4" />Request to hire</Button>
            <Button data-testid="request-interview-button" variant="outline" onClick={() => setInterview(true)} className="h-11 px-6"><CalendarClock className="mr-2 h-4 w-4" />Request interview</Button>
            <Button data-testid="detail-shortlist-button" variant="ghost" onClick={() => toggle(c.id)} className={`h-11 ${on ? "text-amber-700" : ""}`}>
              <Bookmark className="mr-2 h-4 w-4" fill={on ? "currentColor" : "none"} />{on ? "Shortlisted" : "Add to shortlist"}
            </Button>
            <Button data-testid="request-files-button" variant="ghost" onClick={() => setDocReq(true)} className="h-11"><FileLock2 className="mr-2 h-4 w-4" />Request files</Button>
          </div>
        )}
      </header>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Section title="Profile summary"><p className="leading-relaxed text-stone-700" data-testid="candidate-summary">{c.summary}</p></Section>
          <Section title="Work history" icon={BriefcaseBusiness}>
            <ol className="relative space-y-6 border-l border-stone-200 pl-6" data-testid="candidate-work-history">
              {(c.work_history || []).map((w, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[29px] top-1.5 h-2.5 w-2.5 rounded-full bg-slate-900" />
                  <p className="font-display font-bold text-stone-900">{w.role} <span className="font-normal text-stone-500">· {w.company}</span></p>
                  <p className="text-xs font-mono uppercase tracking-wider text-stone-500">{w.period}</p>
                  <p className="mt-1.5 text-sm text-stone-600">{w.description}</p>
                </li>
              ))}
              {!c.work_history?.length && <p className="text-sm text-stone-500">No work history listed.</p>}
            </ol>
          </Section>
          <Section title="Licenses & certifications" icon={ShieldCheck}>
            <LicensesPanel candidate={c} mode={admin ? "admin" : self ? "self" : "view"} onUpdate={setData} />
          </Section>
          {!self && <Section title="Documents" icon={Files}><OwnerDocs cid={c.id} /></Section>}
          <Section title="Education" icon={GraduationCap}>
            {(c.education || []).map((e, i) => (
              <div key={i} className="flex justify-between border-b border-stone-100 py-2 last:border-0 text-sm">
                <span><span className="font-medium text-stone-900">{e.degree}</span> <span className="text-stone-500">· {e.school}</span></span>
                <span className="font-mono text-stone-500">{e.year}</span>
              </div>
            ))}
            {!c.education?.length && <p className="text-sm text-stone-500">No education listed.</p>}
          </Section>
        </div>
        <div className="space-y-6">
          <Section title="Interview availability" icon={CalendarClock}><AvailabilityView schedule={c.availability_schedule} compact /></Section>
          <Section title="Key details">
            <dl className="divide-y divide-stone-100" data-testid="candidate-key-facts">
              {facts.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 py-2.5 text-sm"><dt className="text-stone-500">{k}</dt><dd className="text-right font-medium text-stone-900">{v || "—"}</dd></div>
              ))}
            </dl>
          </Section>
          <Section title="Skills"><Tags items={c.skills} /></Section>
          <Section title="Tools & software"><Tags items={c.tools} /></Section>
        </div>
      </div>

      <HireDialog candidate={c} open={hire} onOpenChange={setHire} />
      <InterviewDialog candidate={c} open={interview} onOpenChange={setInterview} />
      {business && <OfferDialog candidate={c} open={offer} onOpenChange={setOffer} />}
      {business && <DocRequestDialog open={docReq} onOpenChange={setDocReq} targetType="candidate" targetId={c.id} targetName={c.name} />}
      {self && <CandidateForm open={edit} onOpenChange={setEdit} candidate={c} onSaved={setData} saveFn={(p) => api.put("/me/candidate", p)} selfMode />}
    </div>
  );
}
